import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import {
  BookmarkSimpleIcon,
  CalendarDotsIcon,
  CameraIcon,
  CompassIcon,
  MapPinIcon,
  MapTrifoldIcon,
  StampIcon,
  UsersThreeIcon
} from '@phosphor-icons/react';
import { CardTitle, Container, DisplayHeading, Eyebrow, Stamp, type CardArt } from '@/travo/components/ui/postmark';
import { useCopy } from '@/travo/i18n';
import { cycle } from '@/travo/utils/cycle';

/** Per-stop art, in the same order as copy.features.items. */
const STOP_ART: [CardArt, ...CardArt[]] = [
  { icon: MapTrifoldIcon, color: 'sun', tilt: '-1deg' },
  { icon: MapPinIcon, color: 'sky', tilt: '0.8deg' },
  { icon: CameraIcon, color: 'sun', tilt: '-0.6deg' },
  { icon: BookmarkSimpleIcon, color: 'sand', tilt: '1deg' },
  { icon: UsersThreeIcon, color: 'sand', tilt: '0.6deg' },
  { icon: StampIcon, color: 'sun', tilt: '-1deg' },
  { icon: CalendarDotsIcon, color: 'sky', tilt: '0.8deg' },
  { icon: CompassIcon, color: 'sun', tilt: '-0.6deg' }
];

const Features = () => {
  const copy = useCopy();

  return (
    <Container as="section" id="features" pt={{ base: '80px', md: '112px' }} pb="64px">
      <Flex wrap="wrap" align="flex-end" justify="space-between" gap="24px" mb="56px">
        <DisplayHeading fontSize={{ base: '36px', md: '54px' }} maxW="12em">
          {copy.features.title}
        </DisplayHeading>
        <Text maxW="25em" color="inkSoft">
          {copy.features.body}
        </Text>
      </Flex>

      <Grid templateColumns="repeat(auto-fit, minmax(260px, 1fr))" gap="36px 28px">
        {copy.features.items.map((item, i) => {
          const { icon: StopIcon, color: chip, tilt } = cycle(STOP_ART, i);
          return (
            <Stamp key={item.tag} transform={`rotate(${tilt})`}>
              <Flex direction="column" gap="12px" h="100%">
                <Flex justify="space-between" align="center" gap="8px">
                  <Eyebrow fontSize="11px" color="ink">
                    {item.tag}
                  </Eyebrow>
                  <Flex
                    flex="none"
                    w="48px"
                    h="48px"
                    borderRadius="full"
                    bg={chip}
                    border="2px solid"
                    borderColor="ink"
                    align="center"
                    justify="center"
                  >
                    <StopIcon size={24} weight="duotone" aria-hidden />
                  </Flex>
                </Flex>
                <CardTitle>{item.title}</CardTitle>
                <Text fontSize="16px" color="inkSoft">
                  {item.body}
                </Text>
              </Flex>
            </Stamp>
          );
        })}
      </Grid>

      <Flex wrap="wrap" align="center" gap="12px" mt="56px" fontSize="15px">
        <Eyebrow fontSize="12px" mr="6px">
          {copy.features.extrasLabel}
        </Eyebrow>
        {copy.features.extras.map((extra) => (
          <Box key={extra} border="2px solid" borderColor="ink" borderRadius="full" px="14px" py="6px" bg="card">
            {extra}
          </Box>
        ))}
      </Flex>
    </Container>
  );
};

export default Features;
